import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CButton } from "@coreui/react";
import { CMultiSelect } from "@tradingdroid/coreui-pro";

import { tdApi } from "@src/services/api/tdApi";

import "./TagsEditor.scss";

interface Option {
  value: number | string;
  label: string;
  selected?: boolean;
}

interface Props {
  tags: string[];
  entityType: "backtest" | "execution";
  entityId: number;
  onTagsUpdated: (tags: string[]) => void;
  onError?: (message: string) => void;
}

export const TagsEditor: React.FC<Props> = ({
  tags,
  entityType,
  entityId,
  onTagsUpdated,
  onError,
}) => {
  const [localTags, setLocalTags] = useState<string[]>(tags);
  const [isSaving, setIsSaving] = useState(false);
  const [filterText, setFilterText] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync local state when switching to a different entity
  useEffect(() => {
    setLocalTags(tags);
  }, [entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only pass selected tags as options (for badge rendering)
  const options: Option[] = useMemo(() => {
    return localTags.map((name) => ({ value: name, label: name, selected: true }));
  }, [localTags]);

  const clearSearchInput = useCallback(() => {
    const input = containerRef.current?.querySelector<HTMLInputElement>(
      ".form-multi-select-search",
    );
    if (input) {
      // Use native setter to trigger React's onChange
      const nativeSetter = Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        "value",
      )?.set;
      nativeSetter?.call(input, "");
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }
    setFilterText("");
  }, []);

  const addTag = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      setLocalTags((prev) => {
        if (prev.includes(trimmed)) return prev;
        return [...prev, trimmed];
      });
      clearSearchInput();
    },
    [clearSearchInput],
  );

  // Intercept Tab/Enter to add tag directly
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === "Tab" || e.key === "Enter") && filterText.trim()) {
        e.preventDefault();
        e.stopPropagation();
        addTag(filterText);
      }
    },
    [filterText, addTag],
  );

  // Track CMultiSelect's onChange for tag removal (badges x button)
  const handleChange = useCallback((selected: Option[]) => {
    setLocalTags(selected.map((o) => String(o.value)));
  }, []);

  const handleFilterChange = useCallback((value: string) => {
    setFilterText(value);
  }, []);

  const hasChanges = useMemo(() => {
    if (localTags.length !== tags.length) return true;
    const sorted1 = [...localTags].sort();
    const sorted2 = [...tags].sort();
    return sorted1.some((v, i) => v !== sorted2[i]);
  }, [localTags, tags]);

  const handleSave = useCallback(async () => {
    if (!hasChanges) return;
    setIsSaving(true);
    try {
      if (entityType === "backtest") {
        await tdApi.patchBacktest(entityId, { tags: localTags });
      } else {
        await tdApi.patchExecution(entityId, { tags: localTags });
      }
      onTagsUpdated(localTags);
    } catch (error) {
      console.error("[TagsEditor] Save failed:", error);
      onError?.("Failed to save tags");
    } finally {
      setIsSaving(false);
    }
  }, [hasChanges, entityType, entityId, localTags, onTagsUpdated, onError]);

  return (
    <div className="TagsEditor">
      <div className="TagsEditorRow">
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          className="TagsEditorSelect TagsEditorHideDropdown"
          ref={containerRef}
          onKeyDown={handleKeyDown}
        >
          <CMultiSelect
            options={options}
            onChange={handleChange}
            onFilterChange={handleFilterChange}
            allowCreateOptions
            clearSearchOnSelect
            selectionType="tags"
            size="sm"
            placeholder="Add tags..."
            search
            selectAll={false}
          />
        </div>
        <CButton
          color="primary"
          size="sm"
          className="TagsEditorSaveBtn"
          disabled={!hasChanges || isSaving}
          onClick={handleSave}
        >
          {isSaving ? "Saving..." : "Save"}
        </CButton>
      </div>
    </div>
  );
};
